import React, { useEffect, useState, useRef } from 'react';
import { api } from './auth';

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef();

  const load = async () => {
    const r = await api.get('/admin/settings');
    setSettings(r.data);
    setLogoPreview(r.data.has_logo ? `${process.env.REACT_APP_API_URL || ''}/platform-settings/logo?t=${Date.now()}` : null);
  };
  useEffect(() => { load(); }, []);

  const handleLogoChange = (file) => {
    if (!file) { setLogoFile(null); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    if (!window.confirm('Remove platform logo?')) return;
    await api.delete('/admin/settings/logo');
    setLogoFile(null); setLogoPreview(null);
    load();
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch('/admin/settings', {
        system_name: settings.system_name,
        primary_colour: settings.primary_colour,
        default_from_email: settings.default_from_email,
        email_signature: settings.email_signature,
        privacy_notice: settings.privacy_notice,
        cost_voice_cents: settings.cost_voice_cents,
        cost_ocr_cents: settings.cost_ocr_cents,
        cost_extraction_cents: settings.cost_extraction_cents,
        cost_railway_monthly_cents: settings.cost_railway_monthly_cents,
        max_mandates_per_file: settings.max_mandates_per_file,
        default_retention_days: settings.default_retention_days,
        mfa_required_default: settings.mfa_required_default,
        lockout_max_attempts: settings.lockout_max_attempts,
        lockout_window_minutes: settings.lockout_window_minutes,
        review_score_threshold: settings.review_score_threshold,
      });
      if (logoFile) {
        const fd = new FormData(); fd.append('file', logoFile);
        await api.post('/admin/settings/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!settings) return <div style={card}><p style={{ color: '#9CA3AF' }}>Loading…</p></div>;

  return (
    <form onSubmit={save} style={card}>
      <h2 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 4px 0' }}>Platform settings</h2>
      <p style={{ fontSize: 12, color: '#6B7280', marginBottom: 24 }}>System-wide branding, defaults, and cost rates. Changes take effect after saving.</p>

      <Section title="Branding">
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <label style={{ fontSize: 11, color: '#6B7280' }}>Platform logo</label>
            <div onClick={() => logoRef.current.click()} style={{
              width: 120, height: 120, borderRadius: 12,
              background: logoPreview ? '#fff' : '#F3F4F6',
              border: `2px dashed ${logoPreview ? 'transparent' : '#D1D5DB'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', overflow: 'hidden',
            }}>
              {logoPreview
                ? <img src={logoPreview} alt="" style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }} />
                : <div style={{ textAlign: 'center', color: '#9CA3AF' }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    <p style={{ fontSize: 11, margin: '4px 0 0 0' }}>Upload</p>
                  </div>}
              <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/svg+xml" style={{ display: 'none' }} onChange={e => handleLogoChange(e.target.files[0])} />
            </div>
            {logoPreview && <button type="button" onClick={removeLogo} style={{ ...btnGhost, fontSize: 11, padding: '3px 8px', color: '#DC2626' }}>Remove</button>}
            <p style={{ fontSize: 10, color: '#9CA3AF', margin: 0 }}>Shown in topbar</p>
          </div>

          <div style={{ flex: 1, minWidth: 280, display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
            <Field label="System name">
              <input value={settings.system_name || ''} onChange={e => setSettings({ ...settings, system_name: e.target.value })} style={inp} />
            </Field>
            <Field label="Primary brand colour">
              <input type="color" value={settings.primary_colour || '#111827'} onChange={e => setSettings({ ...settings, primary_colour: e.target.value })} style={{ ...inp, padding: 4, height: 38, cursor: 'pointer' }} />
            </Field>
          </div>
        </div>
      </Section>

      <Section title="Email defaults">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
          <Field label="From address">
            <input type="email" placeholder="noreply@yourcompany.com" value={settings.default_from_email || ''} onChange={e => setSettings({ ...settings, default_from_email: e.target.value })} style={inp} />
          </Field>
          <Field label="Email signature">
            <textarea rows={3} value={settings.email_signature || ''} onChange={e => setSettings({ ...settings, email_signature: e.target.value })} style={{ ...inp, fontFamily: 'inherit' }} />
          </Field>
        </div>
      </Section>

      <Section title="Privacy notice">
        <p style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>Shown on the login page.</p>
        <textarea rows={4} value={settings.privacy_notice || ''} onChange={e => setSettings({ ...settings, privacy_notice: e.target.value })} style={{ ...inp, width: '100%', fontFamily: 'inherit' }} />
      </Section>

      <Section title="Cost rates">
        <p style={{ fontSize: 11, color: '#6B7280', marginBottom: 10 }}>Used for cost calculation. All amounts in cents.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
          <CostInput label="Voice transcription" hint="OpenAI Whisper, per voice" value={settings.cost_voice_cents} onChange={v => setSettings({ ...settings, cost_voice_cents: v })} />
          <CostInput label="PDF OCR" hint="Anthropic Vision, per page" value={settings.cost_ocr_cents} onChange={v => setSettings({ ...settings, cost_ocr_cents: v })} />
          <CostInput label="Field extraction" hint="Anthropic Sonnet, per mandate" value={settings.cost_extraction_cents} onChange={v => setSettings({ ...settings, cost_extraction_cents: v })} />
          <CostInput label="Hosting / month" hint="Railway base infra" value={settings.cost_railway_monthly_cents} onChange={v => setSettings({ ...settings, cost_railway_monthly_cents: v })} />
        </div>
      </Section>

      <Section title="Processing limits">
        <p style={{ fontSize: 11, color: '#6B7280', marginBottom: 10 }}>Safety caps applied during verification.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 }}>Max mandates per file</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="number" min={1} max={50} step={1}
                value={settings.max_mandates_per_file || 10}
                onChange={e => setSettings({ ...settings, max_mandates_per_file: parseInt(e.target.value) || 10 })}
                style={{ ...inp, flex: 1 }} />
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>mandates</span>
            </div>
            <p style={{ fontSize: 10, color: '#9CA3AF', margin: '3px 0 0 0' }}>Files with more than this many mandates are rejected (1-50).</p>
          </div>
        </div>
      </Section>

      <Section title="Security and retention">
        <p style={{ fontSize: 11, color: '#6B7280', marginBottom: 10 }}>Platform defaults. A company can override report retention and the login lockout policy on its own record; these apply where the company has not set its own value.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 }}>Default report retention</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="number" min={1} max={3650} step={1}
                value={settings.default_retention_days ?? 7}
                onChange={e => setSettings({ ...settings, default_retention_days: parseInt(e.target.value) || 1 })}
                style={{ ...inp, flex: 1 }} />
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>days</span>
            </div>
            <p style={{ fontSize: 10, color: '#9CA3AF', margin: '3px 0 0 0' }}>How long a generated report file is kept before deletion.</p>
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 }}>Review score threshold</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="number" min={0} max={100} step={1}
                value={settings.review_score_threshold ?? 70}
                onChange={e => setSettings({ ...settings, review_score_threshold: parseInt(e.target.value) })}
                style={{ ...inp, flex: 1 }} />
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>/ 100</span>
            </div>
            <p style={{ fontSize: 10, color: '#9CA3AF', margin: '3px 0 0 0' }}>Results scoring below this are flagged for human review.</p>
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 }}>Login lockout threshold</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="number" min={1} max={50} step={1}
                value={settings.lockout_max_attempts ?? 5}
                onChange={e => setSettings({ ...settings, lockout_max_attempts: parseInt(e.target.value) || 1 })}
                style={{ ...inp, flex: 1 }} />
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>attempts</span>
            </div>
            <p style={{ fontSize: 10, color: '#9CA3AF', margin: '3px 0 0 0' }}>Failed sign-ins before an account is temporarily locked.</p>
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 }}>Lockout window</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="number" min={1} max={1440} step={1}
                value={settings.lockout_window_minutes ?? 15}
                onChange={e => setSettings({ ...settings, lockout_window_minutes: parseInt(e.target.value) || 1 })}
                style={{ ...inp, flex: 1 }} />
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>minutes</span>
            </div>
            <p style={{ fontSize: 10, color: '#9CA3AF', margin: '3px 0 0 0' }}>How long the lock lasts, and the window failed attempts are counted over. Override per company on the company record.</p>
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!settings.mfa_required_default} onChange={e => setSettings({ ...settings, mfa_required_default: e.target.checked })} style={{ width: 16, height: 16, marginTop: 2, cursor: 'pointer' }} />
          <span>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>Require 2FA for new companies by default</span>
            <span style={{ display: 'block', fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>Applies to companies created from here on. Enforcement at sign-in activates with the enrollment gate.</span>
          </span>
        </label>
      </Section>

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save settings'}</button>
      </div>

      <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 14 }}>Last updated: {settings.updated_at ? new Date(settings.updated_at).toLocaleString() : '-'}</p>
    </form>
  );
}

function Section({ title, children }) {
  return <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: '0.5px solid #F3F4F6' }}>
    <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>{title}</p>
    {children}
  </div>;
}
function Field({ label, children }) {
  return <div>
    <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 }}>{label}</label>
    {children}
  </div>;
}
function CostInput({ label, hint, value, onChange }) {
  return <div>
    <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 }}>{label}</label>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 12, color: '#6B7280' }}>R</span>
      <input type="number" min={0} step={0.01} value={((value || 0) / 100).toFixed(2)} onChange={e => onChange(Math.round(parseFloat(e.target.value || 0) * 100))} style={{ ...inp, flex: 1, textAlign: 'right' }} />
      <span style={{ fontSize: 11, color: '#9CA3AF' }}>({value || 0}c)</span>
    </div>
    <p style={{ fontSize: 10, color: '#9CA3AF', margin: '3px 0 0 0' }}>{hint}</p>
  </div>;
}

const card = { background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '24px 28px' };
const btnPrimary = { background: '#111827', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const btnGhost = { background: 'transparent', color: '#374151', border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 };
const inp = { padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
